import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Database,
  Search,
  ArrowUpDown,
  ExternalLink,
  Loader2,
  CheckCircle2,
  XCircle,
  Download,
  Eye,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Shield,
  DollarSign,
  TrendingDown,
  Mail,
} from "lucide-react";
import { Link } from "react-router-dom";
import { ComplianceNoticeModal, type ComplianceHospital } from "@/components/ComplianceNoticeModal";
import { ShareOnXButton } from "@/components/ShareOnXButton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useDebounce } from "@/hooks/useDebounce";
import { useTranslation } from 'react-i18next';

type SortField = 'name' | 'state' | 'total_procedures' | 'cpt_count' | 'drg_count' | 'revenue_count' | 'hcpcs_count' | 'ndc_count';
type SortOrder = 'asc' | 'desc';

const PAGE_SIZE = 50;

// ── Compliance helpers ────────────────────────────────────────────────────────

type ComplianceLevel = 'compliant' | 'partial' | 'no_data';

/**
 * Derive a compliance level from what we've imported.
 * Note: this reflects *our data snapshot*, not a live URL check.
 * A hospital with no data may still have a live chargemaster URL —
 * but absence of data is itself a signal worth surfacing.
 */
function getComplianceLevel(hospital: any): ComplianceLevel {
  if (!hospital.has_uploaded_file || !hospital.total_procedures) return 'no_data';
  const missing = getMissingCategories(hospital);
  if (missing.length >= 2) return 'partial';
  return 'compliant';
}

function getMissingCategories(hospital: any): string[] {
  const missing: string[] = [];
  if (!hospital.cpt_count)     missing.push('CPT');
  if (!hospital.drg_count)     missing.push('DRG');
  if (!hospital.revenue_count) missing.push('REV');
  return missing;
}

// Estimated daily fine under 45 CFR Part 180.
// CMS uses $300/day for hospitals <30 beds, $10/bed/day otherwise (max $5,500/day).
// Without bed count per-row we use the worst-case max per non-compliant hospital.
// This is displayed as "≥ $X/day" to reflect it's a minimum exposure, not a ceiling.
function getEstDailyFine(bedCount: number | null): string {
  if (!bedCount) return '≥ $300/day';
  if (bedCount < 30) return '$300/day';
  const fine = Math.min(bedCount * 10, 5500);
  return `$${fine.toLocaleString()}/day`;
}

// Law effective date: January 1, 2021
const LAW_EFFECTIVE = new Date('2021-01-01');
const TODAY = new Date();
const DAYS_LAW_ACTIVE = Math.floor(
  (TODAY.getTime() - LAW_EFFECTIVE.getTime()) / (1000 * 60 * 60 * 24)
);

// Rough fine gap estimate for the summary banner
// ~3,000 non-compliant hospitals × avg fine of $2,000/day × days active
// This is a conservative illustrative figure, not legal advice
const EST_FINE_GAP_TOTAL = (3000 * 2000 * DAYS_LAW_ACTIVE);
const CMS_COLLECTED = 1500000; // ~$1.5M actually collected

const DataSources = () => {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const { t } = useTranslation('legal');
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>('total_procedures');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState(0);
  const [complianceFilter, setComplianceFilter] = useState<ComplianceLevel | 'all'>('all');
  const [noticeHospital, setNoticeHospital] = useState<ComplianceHospital | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => { setPage(0); }, [debouncedSearch, complianceFilter]);

  // Summary stats — cached separately
  const { data: summary } = useQuery({
    queryKey: ['data-sources-summary-stats'],
    queryFn: async () => {
      const { count: totalCount } = await supabase
        .from('data_sources_summary')
        .select('id', { count: 'exact', head: true });

      const { count: withFilesCount } = await supabase
        .from('data_sources_summary')
        .select('id', { count: 'exact', head: true })
        .eq('has_uploaded_file', true);

      return {
        total: totalCount || 0,
        withFiles: withFilesCount || 0,
        pending: (totalCount || 0) - (withFilesCount || 0),
      };
    },
  });

  // Paginated rows
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['data-sources-page', debouncedSearch, sortField, sortOrder, page],
    queryFn: async () => {
      const dbSortField: Record<SortField, string> = {
        name: 'name',
        state: 'state',
        total_procedures: 'total_procedures',
        cpt_count: 'cpt_count',
        drg_count: 'drg_count',
        revenue_count: 'revenue_count',
        hcpcs_count: 'hcpcs_count',
        ndc_count: 'ndc_count',
      };

      let query = supabase
        .from('data_sources_summary')
        .select('*', { count: 'exact' })
        .order('has_uploaded_file', { ascending: false })
        .order(dbSortField[sortField], { ascending: sortOrder === 'asc' })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (debouncedSearch.trim()) {
        const term = `%${debouncedSearch.trim()}%`;
        query = query.or(`name.ilike.${term},city.ilike.${term},state.ilike.${term}`);
      }

      const { data: rows, count, error } = await query;
      if (error) throw error;

      return {
        rows: rows || [],
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / PAGE_SIZE),
      };
    },
    placeholderData: (prev) => prev,
  });

  // Apply client-side compliance filter after fetch
  const allHospitals = data?.rows || [];
  const hospitals = complianceFilter === 'all'
    ? allHospitals
    : allHospitals.filter((h: any) => getComplianceLevel(h) === complianceFilter);

  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 0;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return <ArrowUpDown className={`w-4 h-4 ml-1 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />;
  };

  // AHP brand colors (dark-mode aware)
  const cream  = ahpDark ? '#0a1628' : '#FAF9F6';
  const green  = ahpDark ? '#6bc9a0' : '#2b5e49';
  const navy   = ahpDark ? '#7da6d4' : '#002868';
  const gold   = '#C5A55A';
  const red    = ahpDark ? '#e85d6c' : '#B22234';
  const ink    = ahpDark ? '#f0ebe3' : '#1B1B1B';
  const muted  = ahpDark ? '#9ca3af' : '#6b7280';
  const border = ahpDark ? 'rgba(197,165,90,0.2)' : '#e8d4b0';
  const cardBg = ahpDark ? '#0f1d2f' : '#fff';
  const serif  = "'Instrument Serif', Georgia, serif";
  const sans   = "'Instrument Sans', system-ui, sans-serif";

  // ── Compliance badge ────────────────────────────────────────────────────
  const ComplianceBadge = ({ hospital }: { hospital: any }) => {
    const level = getComplianceLevel(hospital);
    if (isAhp) {
      if (level === 'compliant') return (
        <Badge className="text-xs text-white whitespace-nowrap" style={{ backgroundColor: green, border: 'none' }}>
          <CheckCircle2 className="w-3 h-3 mr-1" /> Data Available
        </Badge>
      );
      if (level === 'partial') return (
        <Badge className="text-xs whitespace-nowrap" style={{ backgroundColor: `${gold}20`, color: ahpDark ? '#d4b65e' : '#92710f', border: `1px solid ${gold}60` }}>
          <AlertTriangle className="w-3 h-3 mr-1" /> Partial
        </Badge>
      );
      return (
        <Badge className="text-xs whitespace-nowrap" style={{ backgroundColor: `${red}15`, color: red, border: `1px solid ${red}40` }}>
          <XCircle className="w-3 h-3 mr-1" /> No Data
        </Badge>
      );
    }
    // Luigi's
    if (level === 'compliant') return <Badge className="bg-terminal-green/20 text-terminal-green border-terminal-green/30 text-xs">Data Available</Badge>;
    if (level === 'partial')   return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">Partial</Badge>;
    return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">No Data</Badge>;
  };

  // ── Missing categories chips ────────────────────────────────────────────
  const MissingChips = ({ hospital }: { hospital: any }) => {
    if (!hospital.has_uploaded_file) return null;
    const missing = getMissingCategories(hospital);
    if (missing.length === 0) return (
      <span className="text-xs" style={isAhp ? { color: green } : undefined}>
        <CheckCircle2 className="w-3 h-3 inline mr-1" />All present
      </span>
    );
    return (
      <div className="flex flex-wrap gap-1">
        {missing.map(cat => (
          <span
            key={cat}
            className="text-xs px-1.5 py-0.5 rounded font-mono"
            style={isAhp
              ? { backgroundColor: `${red}15`, color: red, border: `1px solid ${red}30` }
              : undefined
            }
          >
            −{cat}
          </span>
        ))}
      </div>
    );
  };

  const today = new Date().toISOString().split('T')[0];
  const canonicalUrl = `${brand.primaryDomain}/data-sources`;

  const seoTitle = isAhp
    ? `Hospital Price Transparency Compliance Tracker | ${brand.name}`
    : `Hospital Chargemaster Data Sources | ${brand.name}`;

  const seoDescription = isAhp
    ? `Track federal price transparency compliance for 9,500+ US hospitals. See chargemaster data coverage, missing billing code categories, and estimated CMS fine exposure under 45 CFR Part 180. The law has been in effect since January 1, 2021. CMS has collected approximately $1.5M in fines. The estimated fine gap exceeds $5 billion.`
    : `Comprehensive overview of all hospital chargemaster data in our database. View imported pricing transparency files for each of the 9,500+ hospitals we track, including CPT, DRG, revenue, and drug code coverage.`;

  const seoKeywords = isAhp
    ? "hospital price transparency compliance, 45 CFR Part 180, non-compliant hospitals, chargemaster data, CMS price transparency, hospital chargemaster, missing DRG codes, hospital billing codes, price transparency tracker"
    : "hospital chargemaster, hospital price data, hospital billing codes, CPT codes, DRG codes, hospital transparency data, medical procedure pricing";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "name": isAhp
      ? "Hospital Price Transparency Compliance Database"
      : "Hospital Chargemaster Data Sources",
    "description": seoDescription,
    "url": canonicalUrl,
    "dateModified": today,
    "creator": {
      "@type": "Organization",
      "name": brand.name,
      "url": brand.primaryDomain
    },
    "license": "https://creativecommons.org/licenses/by/4.0/",
    "keywords": [
      "hospital price transparency",
      "chargemaster",
      "45 CFR Part 180",
      "CPT codes",
      "DRG codes",
      "hospital billing"
    ],
    "spatialCoverage": {
      "@type": "Place",
      "name": "United States"
    },
    "temporalCoverage": "2021-01-01/..",
    "isAccessibleForFree": true
  };

  return (
    <div className={isAhp ? "min-h-screen pb-24 md:pb-8" : "min-h-screen pb-24 md:pb-8 bg-background"} style={isAhp ? { backgroundColor: cream, color: ink } : undefined}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        {/* Open Graph */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={brand.name} />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={brand.social.ogImage} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ── Watchdog Banner (AHP only) ────────────────────────────────────── */}
      {isAhp && (
        <div
          className="border-b"
          style={{ background: `linear-gradient(135deg, ${navy} 0%, #001540 100%)`, borderColor: `${navy}40` }}
        >
          <div className="mx-auto px-5 sm:px-6 py-5 max-w-7xl">
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <div className="flex items-start gap-4 flex-1 min-w-0">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${red}33` }}
              >
                <Shield className="w-5 h-5" style={{ color: red }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span
                    className="text-xs font-bold uppercase tracking-widest"
                    style={{ color: gold, fontFamily: sans }}
                  >
                    Consumer Watchdog
                  </span>
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>·</span>
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: sans }}>
                    Price Transparency Compliance Tracker
                  </span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)', fontFamily: sans }}>
                  The Hospital Price Transparency Rule (45 CFR Part 180) has been federal law since{' '}
                  <strong className="text-white">January 1, 2021</strong> — that's{' '}
                  <strong className="text-white">{DAYS_LAW_ACTIVE.toLocaleString()} days</strong> ago.
                  Only 21% of hospitals are fully compliant. CMS has issued{' '}
                  <strong className="text-white">27 monetary penalties</strong> total.
                </p>
              </div>
              </div>
              <div className="flex items-center gap-6 flex-wrap md:flex-shrink-0 md:justify-end">
                <div className="text-center">
                  <div className="text-xs uppercase tracking-wider mb-0.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: sans }}>Est. Fines Owed</div>
                  <div className="text-xl font-bold" style={{ color: red, fontFamily: serif }}>
                    ${(EST_FINE_GAP_TOTAL / 1_000_000_000).toFixed(1)}B+
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs uppercase tracking-wider mb-0.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: sans }}>Actually Collected</div>
                  <div className="text-xl font-bold" style={{ color: gold, fontFamily: serif }}>
                    ${(CMS_COLLECTED / 1_000_000).toFixed(1)}M
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <header
        className={isAhp ? "border-b" : "border-b border-border bg-secondary/30"}
        style={isAhp ? { borderColor: border, background: ahpDark ? 'linear-gradient(135deg, #0f1d2f 0%, #0a1628 100%)' : `linear-gradient(135deg, ${cream} 0%, #f0ebe3 100%)` } : undefined}
      >
        <div className="mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-4">
            <Database className="w-8 h-8" style={isAhp ? { color: green } : undefined} />
            {isAhp ? (
              <h1 className="text-4xl font-bold" style={{ color: ink, fontFamily: serif }}>
                Hospital Compliance Tracker
              </h1>
            ) : (
              <h1 className="text-4xl font-bold text-terminal-green glitch" data-text="Data Sources">
                Data Sources
              </h1>
            )}
          </div>
          <p
            className={isAhp ? "max-w-3xl" : "text-muted-foreground max-w-3xl"}
            style={isAhp ? { color: muted, fontFamily: sans } : undefined}
          >
            {isAhp
              ? "Chargemaster data imported for each hospital — including code coverage and compliance indicators. Hospitals with missing code categories may not be meeting their full disclosure obligations under 45 CFR Part 180."
              : "Comprehensive overview of all hospital chargemaster data in our database. Click \"View Data\" to see the imported pricing transparency data for each hospital."
            }
          </p>
        </div>
      </header>

      {/* ── Content ──────────────────────────────────────────────────────── */}
      <main className="mx-auto px-6 py-12">
        <Card
          className={isAhp ? "p-6 shadow-sm" : "terminal-card p-6"}
          style={isAhp ? { backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: '12px' } : undefined}
        >
          {/* Search, Filters and Compliance Toggle */}
          <div className="mb-6 flex gap-4 items-center flex-wrap">
            <div className="relative flex-1 min-w-[280px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                type="text"
                placeholder="Search hospitals, cities, or states..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                style={isAhp ? { borderColor: border, backgroundColor: ahpDark ? '#0a1628' : '#FAF9F6', color: ink } : undefined}
              />
            </div>

            {/* Compliance filter pills */}
            <div className="flex gap-2 flex-wrap">
              {(['all', 'compliant', 'partial', 'no_data'] as const).map((f) => {
                const labels: Record<typeof f, string> = {
                  all: 'All',
                  compliant: 'Data Available',
                  partial: 'Partial',
                  no_data: 'No Data',
                };
                const active = complianceFilter === f;
                const activeColor = f === 'compliant' ? green : f === 'partial' ? gold : f === 'no_data' ? red : navy;
                return (
                  <button
                    key={f}
                    onClick={() => setComplianceFilter(f)}
                    className="text-xs px-3 py-1.5 rounded-full font-medium transition-all"
                    style={isAhp ? {
                      backgroundColor: active ? activeColor : 'transparent',
                      color: active ? '#fff' : muted,
                      border: `1px solid ${active ? activeColor : border}`,
                      fontFamily: sans,
                    } : undefined}
                  >
                    {labels[f]}
                  </button>
                );
              })}
            </div>

            <div className="flex gap-2 items-center ml-auto">
              {isFetching && !isLoading && (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              )}
              <Badge variant="outline" className="text-sm" style={isAhp ? { borderColor: border, color: muted } : undefined}>
                {totalCount.toLocaleString()} hospitals
              </Badge>
              {summary && (
                <>
                  <Badge
                    className={isAhp ? "text-sm text-white" : "bg-terminal-green text-black text-sm"}
                    style={isAhp ? { backgroundColor: green } : undefined}
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {summary.withFiles.toLocaleString()} with data
                  </Badge>
                  <Badge
                    variant="secondary"
                    className={isAhp ? "text-sm" : "bg-yellow-500/20 text-yellow-500 border-yellow-500/30 text-sm"}
                    style={isAhp ? { backgroundColor: `${gold}20`, color: gold, border: `1px solid ${gold}30` } : undefined}
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    {summary.pending.toLocaleString()} missing
                  </Badge>
                </>
              )}
            </div>
          </div>

          {/* Data Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={isAhp ? { color: green } : undefined} />
              <span className="ml-3 text-muted-foreground">Loading compliance data...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={isAhp ? { borderColor: border } : undefined}>
                    <TableHead className="min-w-[260px]">
                      <button onClick={() => handleSort('name')} className="flex items-center hover:text-primary transition-colors">
                        Hospital Name {getSortIcon('name')}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('state')} className="flex items-center hover:text-primary transition-colors">
                        State {getSortIcon('state')}
                      </button>
                    </TableHead>
                    {isAhp && (
                      <TableHead className="min-w-[120px]">
                        <span className="flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          Status
                        </span>
                      </TableHead>
                    )}
                    {isAhp && (
                      <TableHead className="min-w-[140px]">
                        <span className="flex items-center gap-1">
                          <TrendingDown className="w-3 h-3" />
                          Missing Codes
                        </span>
                      </TableHead>
                    )}
                    <TableHead className="text-center">View Data</TableHead>
                    <TableHead className="text-center">Download</TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('total_procedures')} className="flex items-center ml-auto hover:text-primary transition-colors">
                        Total Procedures {getSortIcon('total_procedures')}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('cpt_count')} className="flex items-center ml-auto hover:text-primary transition-colors">
                        CPT {getSortIcon('cpt_count')}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('drg_count')} className="flex items-center ml-auto hover:text-primary transition-colors">
                        DRG {getSortIcon('drg_count')}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('revenue_count')} className="flex items-center ml-auto hover:text-primary transition-colors">
                        REV {getSortIcon('revenue_count')}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('hcpcs_count')} className="flex items-center ml-auto hover:text-primary transition-colors">
                        HCPCS {getSortIcon('hcpcs_count')}
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleSort('ndc_count')} className="flex items-center ml-auto hover:text-primary transition-colors">
                        NDC {getSortIcon('ndc_count')}
                      </button>
                    </TableHead>
                    {isAhp && (
                      <TableHead className="text-right min-w-[110px]">
                        <span className="flex items-center justify-end gap-1">
                          <DollarSign className="w-3 h-3" />
                          Est. Fine/Day
                        </span>
                      </TableHead>
                    )}
                    {isAhp && (
                      <TableHead className="text-center min-w-[90px]">
                        <span className="flex items-center justify-center gap-1">
                          <Mail className="w-3 h-3" />
                          Act
                        </span>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hospitals.map((hospital: any) => {
                    const compliance = getComplianceLevel(hospital);
                    const isNonCompliant = compliance !== 'compliant';
                    return (
                      <TableRow
                        key={hospital.id}
                        style={isAhp
                          ? {
                              borderColor: border,
                              backgroundColor: isNonCompliant && !hospital.has_uploaded_file
                                ? (ahpDark ? 'rgba(232,93,108,0.06)' : `#B2223404`)
                                : undefined,
                            }
                          : undefined
                        }
                      >
                        <TableCell className="font-medium">
                          <Link
                            to={`/hospital/${hospital.id}`}
                            className="hover:text-primary transition-colors flex items-center gap-2"
                            style={isAhp ? { color: ink } : undefined}
                          >
                            {hospital.name}
                            <ExternalLink className="w-3 h-3 opacity-50" />
                          </Link>
                          <div className="text-xs text-muted-foreground mt-1">
                            {hospital.city}, {hospital.state}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" style={isAhp ? { borderColor: border, color: navy, backgroundColor: ahpDark ? 'rgba(125,166,212,0.1)' : undefined } : undefined}>
                            {hospital.state}
                          </Badge>
                        </TableCell>
                        {isAhp && (
                          <TableCell>
                            <ComplianceBadge hospital={hospital} />
                          </TableCell>
                        )}
                        {isAhp && (
                          <TableCell>
                            <MissingChips hospital={hospital} />
                          </TableCell>
                        )}
                        <TableCell className="text-center">
                          {hospital.has_uploaded_file ? (
                            <Link
                              to={`/hospital/${hospital.id}/chargemasters/`}
                              className={isAhp ? "inline-flex items-center gap-2 transition-colors hover:opacity-80" : "inline-flex items-center gap-2 text-terminal-green hover:text-primary transition-colors"}
                              style={isAhp ? { color: green } : undefined}
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </Link>
                          ) : (
                            <span className="text-sm flex items-center justify-center gap-1 font-medium" style={isAhp ? { color: gold } : undefined}>
                              <XCircle className="w-3 h-3" />
                              Pending
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {hospital.file_download_url ? (
                            <a
                              href={hospital.file_download_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={isAhp ? "inline-flex items-center gap-2 transition-colors hover:opacity-80" : "inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"}
                              style={isAhp ? { color: navy } : undefined}
                              download={hospital.file_name}
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </a>
                          ) : (
                            <span className="text-muted-foreground text-sm flex items-center justify-center gap-1">
                              <XCircle className="w-3 h-3" />
                              N/A
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono" style={isAhp ? { color: ink } : undefined}>
                          {hospital.total_procedures?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {hospital.cpt_count ? (
                            <span style={isAhp ? { color: green } : undefined} className={isAhp ? undefined : "text-terminal-green"}>
                              {hospital.cpt_count.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">&mdash;</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {hospital.drg_count ? (
                            <span style={isAhp ? { color: green } : undefined} className={isAhp ? undefined : "text-terminal-green"}>
                              {hospital.drg_count.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">&mdash;</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {hospital.revenue_count ? (
                            <span style={isAhp ? { color: green } : undefined} className={isAhp ? undefined : "text-terminal-green"}>
                              {hospital.revenue_count.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">&mdash;</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {hospital.hcpcs_count ? (
                            <span style={isAhp ? { color: green } : undefined} className={isAhp ? undefined : "text-terminal-green"}>
                              {hospital.hcpcs_count.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">&mdash;</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {hospital.ndc_count ? (
                            <span style={isAhp ? { color: green } : undefined} className={isAhp ? undefined : "text-terminal-green"}>
                              {hospital.ndc_count.toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">&mdash;</span>
                          )}
                        </TableCell>
                        {isAhp && (
                          <TableCell className="text-right">
                            {!hospital.has_uploaded_file ? (
                              <span
                                className="text-xs font-mono font-semibold whitespace-nowrap"
                                style={{ color: red }}
                              >
                                {getEstDailyFine(hospital.bed_count ?? null)}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        )}
                        {isAhp && (
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => setNoticeHospital(hospital as ComplianceHospital)}
                                title="Send compliance notice"
                                className="p-1 rounded hover:bg-muted transition-colors"
                              >
                                <Mail className="w-3.5 h-3.5" style={{ color: compliance !== 'compliant' ? red : muted }} />
                              </button>
                              <ShareOnXButton hospital={hospital as any} compact />
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {hospitals.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isAhp ? 13 : 10} className="text-center py-12 text-muted-foreground">
                        No hospitals found matching your search.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t" style={isAhp ? { borderColor: border } : undefined}>
              <div className="text-sm text-muted-foreground">
                Showing {(page * PAGE_SIZE + 1).toLocaleString()}–{Math.min((page + 1) * PAGE_SIZE, totalCount).toLocaleString()} of {totalCount.toLocaleString()}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 7) {
                      pageNum = i;
                    } else if (page < 3) {
                      pageNum = i;
                    } else if (page > totalPages - 4) {
                      pageNum = totalPages - 7 + i;
                    } else {
                      pageNum = page - 3 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? "default" : "outline"}
                        size="sm"
                        className="w-9 h-9 p-0"
                        onClick={() => setPage(pageNum)}
                        style={isAhp && page === pageNum ? { backgroundColor: ahpDark ? '#7da6d4' : '#002868', borderColor: ahpDark ? '#7da6d4' : '#002868', color: ahpDark ? '#0a1628' : '#fff' } : undefined}
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Summary Statistics */}
          {summary && (
            <div className="mt-6 pt-6 border-t" style={isAhp ? { borderColor: border } : undefined}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Total Hospitals</div>
                  <div
                    className={isAhp ? "text-2xl font-bold" : "text-2xl font-mono font-bold text-terminal-green"}
                    style={isAhp ? { color: red, fontFamily: serif } : undefined}
                  >
                    {summary.total.toLocaleString()}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">With Chargemaster Data</div>
                  <div
                    className={isAhp ? "text-2xl font-bold" : "text-2xl font-mono font-bold text-terminal-green"}
                    style={isAhp ? { color: green, fontFamily: serif } : undefined}
                  >
                    {summary.withFiles.toLocaleString()}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Missing Data</div>
                  <div
                    className={isAhp ? "text-2xl font-bold" : "text-2xl font-mono font-bold text-terminal-green"}
                    style={isAhp ? { color: gold, fontFamily: serif } : undefined}
                  >
                    {summary.pending.toLocaleString()}
                  </div>
                </div>
                {isAhp && (
                  <div className="text-center">
                    <div className="text-sm text-muted-foreground mb-1">Est. Fine Gap Since 2021</div>
                    <div className="text-2xl font-bold" style={{ color: red, fontFamily: serif }}>
                      ${(EST_FINE_GAP_TOTAL / 1_000_000_000).toFixed(1)}B+
                    </div>
                  </div>
                )}
              </div>
              {isAhp && (
                <p className="text-xs text-center mt-4" style={{ color: muted, fontFamily: sans }}>
                  Fine gap estimate assumes ~3,000 non-compliant hospitals at an average of $2,000/day since Jan 1, 2021. CMS maximum fine is $5,500/day.
                  Actual enforcement has collected approximately $1.5M total.{' '}
                  <Link to="/lawsuits" className="underline" style={{ color: ahpDark ? '#7da6d4' : '#002868' }}>
                    See our legal record →
                  </Link>
                </p>
              )}
            </div>
          )}
        </Card>
      </main>

      {/* Compliance notice modal — opened per-row */}
      <ComplianceNoticeModal
        hospital={noticeHospital}
        isOpen={!!noticeHospital}
        onClose={() => setNoticeHospital(null)}
      />
    </div>
  );
};

export default DataSources;
